import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import EventsTab from '../components/admin/EventsTab';
import UsersTab from '../components/admin/UsersTab';
import PendingTab from '../components/admin/PendingTab';
import AuditLogTab from '../components/admin/AuditLogTab';
import ConsoleTab from '../components/admin/console/ConsoleTab';
import ActivityLogTab from '../components/admin/ActivityLogTab';
import TokensTab from '../components/admin/TokensTab';
import CoursesTab from '../components/admin/CoursesTab';

import AdminTestsTab from '../components/admin/AdminTestsTab';
import GamificationTab from '../components/admin/GamificationTab';
import { adminAPI, healthAPI, assignmentRequestsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../version';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type TabType = 'events' | 'users' | 'pending' | 'audit' | 'activity' | 'tokens' | 'console' | 'tests' | 'courses' | 'gamification';

const PURPLE = '#200854';

const Admin: React.FC = () => {
  const { t } = useTranslation();
  const { isTrainerOrAdmin, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editFromUrl = searchParams.get('edit');
  const [activeTab, setActiveTab] = useState<TabType>(isTrainerOrAdmin ? 'events' : 'tests');
  const [editSeriesId, setEditSeriesId] = useState<string | null>(editFromUrl);
  const [backendVersion, setBackendVersion] = useState<string>('...');

  React.useEffect(() => {
    healthAPI.getHealth()
      .then(data => setBackendVersion(data.version))
      .catch(() => setBackendVersion('unavailable'));
  }, []);

  // Handle ?edit=seriesId from calendar kebab menu
  React.useEffect(() => {
    if (editFromUrl) {
      setEditSeriesId(editFromUrl);
      setActiveTab('events');
      setSearchParams({}, { replace: true });
    }
  }, [editFromUrl, setSearchParams]);

  const { data: pendingUsers = [] } = useQuery({
    queryKey: ['pending-users'],
    queryFn: adminAPI.getPendingUsers,
    refetchInterval: 30_000,
    enabled: isAdmin,
  });

  const { data: pendingRequests = [] } = useQuery({
    queryKey: ['pending-assignment-requests'],
    queryFn: assignmentRequestsAPI.getPending,
    refetchInterval: 30_000,
    enabled: isAdmin,
  });

  const pendingCount = pendingUsers.length + pendingRequests.length;

  const tabClass = (tab: TabType) =>
    `py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 whitespace-nowrap ${
      activeTab === tab
        ? 'border-transparent text-white'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  const tabStyle = (tab: TabType) =>
    activeTab === tab ? { borderColor: PURPLE, color: PURPLE } : undefined;

  return (
    <>
    <header className="shadow-md" style={{ backgroundColor: PURPLE }}>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/najsport-logo.png" alt="NajSport" className="h-8 w-auto" />
          <h1 className="text-lg font-bold text-white">Admin</h1>
        </div>
        <button
          onClick={() => navigate('/events')}
          className="flex items-center gap-1 text-sm text-white hover:text-orange-300 transition-colors font-medium"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          {t('najsport.rides')}
        </button>
      </div>
    </header>
    <div className="container mx-auto px-4 py-8">

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6 relative">
        <nav className="-mb-px flex space-x-6 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          {isTrainerOrAdmin && (
            <>
              <button onClick={() => { setEditSeriesId(null); setActiveTab('events'); }} className={tabClass('events')} style={tabStyle('events')}>
                {t('najsport.adminRides')}
              </button>
              <button onClick={() => setActiveTab('users')} className={tabClass('users')} style={tabStyle('users')}>
                {t('najsport.adminUsers')}
              </button>
              {isAdmin && (
                <button onClick={() => setActiveTab('audit')} className={tabClass('audit')} style={tabStyle('audit')}>
                  {t('najsport.adminHistory')}
                </button>
              )}
              <button onClick={() => setActiveTab('courses')} className={tabClass('courses')} style={tabStyle('courses')}>
                {t('najsport.adminCourses')}
              </button>
              {isAdmin && (
                <button onClick={() => setActiveTab('activity')} className={tabClass('activity')} style={tabStyle('activity')}>
                  {t('najsport.adminActivity')}
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setActiveTab('gamification')} className={tabClass('gamification')} style={tabStyle('gamification')}>
                  {t('najsport.adminGamification')}
                </button>
              )}
            </>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'events'  && <EventsTab initialEditSeriesId={editSeriesId} />}
      {activeTab === 'users'   && <UsersTab onNavigateToEvent={(seriesId) => { setEditSeriesId(seriesId); setActiveTab('events'); }} />}
      {activeTab === 'audit'   && <AuditLogTab />}
      {activeTab === 'courses'  && <CoursesTab />}
      {activeTab === 'activity' && <ActivityLogTab />}
      {activeTab === 'gamification' && <GamificationTab />}

      {/* Version info */}
      <div className="mt-8 text-center text-xs text-gray-400">
        Frontend v{APP_VERSION} &middot; Backend v{backendVersion}
      </div>
    </div>
    </>
  );
};

export default Admin;
